/* eslint-disable no-use-before-define, no-unused-vars */
import {
  FlatList,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { RepositColours } from "../../utils/styles";
import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import * as designerActions from "../../redux/actions/designer";
import WebHeading from "../../components/WebHeading";
import { goBack } from "../../utils/goTo";
import Icon from "react-native-vector-icons/FontAwesome";
import {
  BatteryIllustration,
  InverterIllustration,
  SolarIllustration,
} from "./ComponentIllustrations";

const Check = <Icon name="check" size={20} color={RepositColours.blue} />;

const SOLAR_PHASE_OPTIONS = [
  {
    uid: 1,
    label: "Single phase",
  },
  {
    uid: 3,
    label: "Three phase",
  },
];

const PV_CONNECTED_OPTIONS = [
  {
    uid: true,
    label: "DC Solar Connected",
  },
  {
    uid: false,
    label: "DC Solar NotConnected",
  },
];

const styles = StyleSheet.create({
  listItem: {
    maxWidth: 400,
    minWidth: 350,
    height: 110,
    borderBottomColor: "#E2E2E2",
    borderBottomWidth: 1,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 20,
  },
  wrapper: { backgroundColor: "white", flex: 1 },
  heading: {
    marginHorizontal: 20,
  },
  headingText: { fontSize: 24, textAlign: "center", fontWeight: "300" },
  addressBox: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#C8C7CC",
  },
  addressHeader: {
    marginHorizontal: 20,
    backgroundColor: "grey",
  },
  addressText: {
    marginHorizontal: 10,
    marginVertical: 2,
    lineHeight: 25,
    fontWeight: "300",
  },
  text: {
    fontSize: 20,
    lineHeight: 25,
    fontWeight: "300",
    marginHorizontal: 10,
  },
  paraText: {
    textAlign: "left",
    fontSize: 18,
    lineHeight: 25,
    fontWeight: "300",
    marginHorizontal: 30,
    marginTop: 20,
  },
  textInput: {
    fontSize: 16,
    borderRadius: 4,
    height: Platform.OS === "android" ? 40 : 25,
    textAlign: "center",
    backgroundColor: "white",
  },
  textInputWrapper: {
    alignItems: "stretch",
  },
  textInputContainer: {
    marginVertical: 10,
    marginHorizontal: 8,
    borderBottomWidth: Platform.OS === "android" ? 0 : 1,
    borderBottomColor: "#D2D2D2",
  },
  bodyText: {
    marginTop: 10,
  },
});

const ComponentSelectionItem = ({
  uid,
  label,
  image,
  selected,
  onSelected,
}) => {
  return (
    <TouchableOpacity style={styles.listItem} onPress={onSelected}>
      {image}
      <Text style={{ flex: 4, textAlign: "left", paddingLeft: 20 }}>
        {label}
      </Text>
      {selected ? Check : null}
    </TouchableOpacity>
  );
};

const mapStateToProps = (state) => ({
  inverters: state.designer.constraints.inverterTypes,
  batteries: state.designer.constraints.batteryTypes,
  inverterSystems: state.designer.kit.design.inverterSystems,
  gridPhases: state.designer.kit.design.gridPhases,
  context: state.designer.kit.context,
  loading: state.designer.kit.loading,
});

const mapDispatchToProps = (dispatch) => {
  return {
    designerActions: bindActionCreators(designerActions, dispatch),
  };
};

class ComponentSelection extends React.Component {
  handleComponentSelected = (value, propertyName) => {
    const { systemUid, component } = this.props.context;
    this.props.designerActions.updateInverterSystem(systemUid, {
      [propertyName ? propertyName : component]: value,
    });
    goBack(this.props.navigation);
  };

  sortByDisplayName = (element1, element2) => {
    if (element1[0] === "00" || element2[0] == null) {
      return -1;
    } else if (element1[0] == null) {
      return 1;
    } else {
      return element1[1].displayName.localeCompare(element2[1].displayName);
    }
  };

  getInverterItems = () => {
    const systemUid = this.props.context.systemUid;
    const selectedInverter =
      this.props.inverterSystems[systemUid].inverter.value;
    return Object.entries(this.props.inverters)
      .sort((a, b) => this.sortByDisplayName(a, b))
      .map(([uid, details]) => {
        return {
          key: uid,
          label: details.displayName,
          image: <InverterIllustration uid={uid} details={details} />,
          selected: uid === selectedInverter,
          onSelected: () => {
            this.handleComponentSelected(uid);
          },
        };
      });
  };

  getBatteryItems = () => {
    const { context, inverters, batteries, inverterSystems } = this.props;
    const { inverter, battery } = inverterSystems[context.systemUid];

    let toConvert;

    if (inverter.value) {
      const { compatibleBatteryTypes: compatibles, allInOne } =
        inverters[inverter.value];

      if (compatibles.length === 0) {
        toConvert = [];
      } else {
        toConvert = Object.entries(batteries).filter(([uid]) =>
          compatibles.includes(uid)
        );
        if (!allInOne) {
          toConvert.push([null, null]);
        }
      }
    } else {
      toConvert = Object.entries(batteries);
      toConvert.push([null, null]);
    }

    return toConvert
      .sort((a, b) => this.sortByDisplayName(a, b))
      .map(([uid, batteryDeets]) => {
        return {
          key: uid,
          label: batteryDeets ? batteryDeets.displayName : "Not present",
          image: <BatteryIllustration batteryDeets={batteryDeets} />,
          selected: uid === battery.value,
          onSelected: () => this.handleComponentSelected(uid),
        };
      });
  };

  getSolarItems = () => {
    const { context, inverters, inverterSystems } = this.props;
    const { inverter, pvConnected, pvPhases } =
      inverterSystems[context.systemUid];

    if (inverter.value) {
      const pvCompatible = inverters[inverter.value].pvCompatible;
      if (pvCompatible) {
        return [
          {
            key: true,
            label: "Connected",
            image: <SolarIllustration pvConnected={true} />,
            selected: pvConnected.value,
            onSelected: () => this.handleComponentSelected(true, "pvConnected"),
          },
          {
            key: false,
            label: "Not present",
            image: <SolarIllustration pvConnected={false} />,
            selected: !pvConnected.value,
            onSelected: () =>
              this.handleComponentSelected(false, "pvConnected"),
          },
        ];
      } else if (pvCompatible === null) {
        // in this case we need to meter the solar ourselves, so need to know the number of phases
        return [
          {
            key: 1,
            label: "Single phase",
            image: <SolarIllustration pvConnected={true} />,
            selected: pvPhases.value === 1,
            onSelected: () => this.handleComponentSelected(1, "pvPhases"),
          },
          {
            key: 3,
            label: "Three phase",
            image: <SolarIllustration pvConnected={true} />,
            selected: pvPhases.value === 3,
            onSelected: () => this.handleComponentSelected(3, "pvPhases"),
          },
        ];
      }
    }
    return [];
  };

  render() {
    if (Platform.OS === "web" && !this.props.context.systemUid) {
      if (this.props.path === "/ComponentSelection") {
        goBack();
      }
      return null;
    }

    const component = this.props.context.component;

    let title, items;

    switch (component) {
      case "inverter":
        title = "Inverter";
        items = this.getInverterItems();
        break;
      case "battery":
        title = "Battery";
        items = this.getBatteryItems();
        break;
      case "solar":
        title = "Solar";
        items = this.getSolarItems();
        break;
      default:
        title = "";
        items = [];
        break;
    }

    return (
      <View style={styles.wrapper}>
        <WebHeading title={title} />
        <View style={{ flex: 1 }}>
          {Platform.OS !== "web" ? (
            <FlatList
              data={items}
              renderItem={({ item }) => <ComponentSelectionItem {...item} />}
            />
          ) : (
            <ScrollView>
              {items.map((item) => (
                <ComponentSelectionItem {...item} />
              ))}
            </ScrollView>
          )}
        </View>
      </View>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ComponentSelection);



// WEBPACK FOOTER //
// ./src/containers/kit/ComponentSelection.js